/** Jarvis Knowledge — documents in a pack with status, version and actions. */
import React, { useRef } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { StatusPill, btnSmall, errorText } from '../finance/financeUi';
import { upload } from './PackList';

const when = (d) => (d ? new Date(d).toLocaleDateString() : '—');

function Actions({ s, onChange }) {
  const fileRef = useRef(null);
  const act = async (path, msg, body) => {
    try { await api.request(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }); toast.success(msg); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  const rename = async () => {
    const title = window.prompt('New title', s.title); if (!title) return;
    try { await api.request(`/jarvis/sources/${s.source_id}`, { method: 'PATCH', body: JSON.stringify({ title }) }); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  const replace = async (file) => {
    if (!file) return;
    try { await upload(`/jarvis/sources/${s.source_id}/replace-pdf`, file); toast.success('New version uploaded — processing'); onChange(); } catch (e) { toast.error(e.message); }
  };
  const editNote = () => {
    const text = window.prompt('Replace the note text (creates a new version)'); if (text) act(`/jarvis/sources/${s.source_id}/note`, 'New note version — processing', { text });
  };
  const id = s.source_id;
  return (
    <span className="flex flex-wrap gap-1">
      <button type="button" className={btnSmall} onClick={rename} data-testid={`source-rename-${id}`}>Rename</button>
      {s.source_type === 'WEB' && <button type="button" className={btnSmall} onClick={() => act(`/jarvis/sources/${id}/refresh`, 'Re-reading the page')} data-testid={`source-refresh-${id}`}>Refresh</button>}
      {s.source_type === 'PDF' && <><button type="button" className={btnSmall} onClick={() => fileRef.current?.click()} data-testid={`source-replace-${id}`}>Replace</button><input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => replace(e.target.files[0])} /></>}
      {s.source_type === 'NOTE' && <button type="button" className={btnSmall} onClick={editNote} data-testid={`source-edit-${id}`}>Edit</button>}
      {s.status === 'DISABLED'
        ? <button type="button" className={btnSmall} onClick={() => act(`/jarvis/sources/${id}/enable`, 'Enabled')} data-testid={`source-enable-${id}`}>Enable</button>
        : <button type="button" className={btnSmall} onClick={() => act(`/jarvis/sources/${id}/disable`, 'Disabled — Jarvis stops using it now')} data-testid={`source-disable-${id}`}>Disable</button>}
      <button type="button" className={btnSmall} onClick={() => window.confirm(`Remove "${s.title}"?`) && act(`/jarvis/sources/${id}/retire`, 'Removed')} data-testid={`source-remove-${id}`}>Remove</button>
    </span>
  );
}

export function SourceList({ rows, onChange }) {
  if (!rows.length) return <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="jarvis-sources-empty">No knowledge in this pack yet. Add a PDF, link or note below.</p>;
  return (
    <ul className="divide-y" data-testid="jarvis-sources">
      {rows.map((s) => {
        const v = s.latest_version || {};
        const a = s.active_version || {};
        return (
          <li key={s.source_id} className="space-y-1.5 px-5 py-3 text-sm" data-testid={`source-${s.source_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">{s.title} <span className="text-xs font-normal text-muted-foreground">· {s.source_type}</span></span>
              <span className="flex items-center gap-2">
                <StatusPill value={s.status} testid={`source-status-${s.source_id}`} />
                {v.processing_status && v.version !== a.version && <StatusPill value={`V${v.version}_${v.processing_status}`} testid={`source-latest-${s.source_id}`} />}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {a.version ? `Live version v${a.version}` : 'No live version yet'}{a.page_count ? ` · ${a.page_count} page${a.page_count === 1 ? '' : 's'}` : ''}{a.chunk_count ? ` · ${a.chunk_count} passages` : ''} · added {when(s.created_at)}
              {a.captured_at ? ` · captured ${when(a.captured_at)}` : ''}{a.final_url ? ` · ${a.final_url}` : ''}
            </p>
            {(v.failure_reason || s.last_failure) && <p className="text-xs text-rose-700" data-testid={`source-failure-${s.source_id}`}>Failed: {v.failure_reason || s.last_failure}</p>}
            {v.processing_status === 'UNCHANGED' && <p className="text-xs text-muted-foreground">Last refresh found no changes.</p>}
            <Actions s={s} onChange={onChange} />
          </li>
        );
      })}
    </ul>
  );
}
